// src/components/CacheDevPanel/index.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useCacheDevTools } from '../../utils/cacheDevTools';
import type { CacheKeyEntry } from '../../utils/cacheDevTools';

const DOT_STYLE: React.CSSProperties = {
  width: 7, height: 7, borderRadius: '50%',
  background: '#7F77DD', display: 'inline-block', marginRight: 5, flexShrink: 0,
};

const BADGE_STYLE: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center',
  background: 'rgba(238,237,254,0.95)',
  border: '1.5px dashed #7F77DD',
  borderRadius: 6, padding: '2px 7px',
  fontSize: 11, fontFamily: 'monospace',
  color: '#3C3489', whiteSpace: 'nowrap',
  marginBottom: 3,
};

const X_BTN: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: '#993556', fontSize: 13, marginLeft: 5,
  padding: 0, lineHeight: 1,
};

const FAB_STYLE: React.CSSProperties = {
  position: 'fixed', bottom: 24, right: 24, zIndex: 99999,
  display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8,
};

const FAB_BTN: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  background: '#3C3489', color: '#EEEDFE',
  border: 'none', borderRadius: 24,
  padding: '10px 20px', fontSize: 13, fontWeight: 500,
  cursor: 'pointer', boxShadow: '0 2px 12px rgba(60,50,137,0.25)',
};

const COUNT_BADGE: React.CSSProperties = {
  background: '#7F77DD', color: '#EEEDFE',
  borderRadius: 12, fontSize: 11, padding: '1px 7px', fontWeight: 500,
};

const PANEL_STYLE: React.CSSProperties = {
  background: 'rgba(255,255,255,0.97)',
  border: '1px solid #AFA9EC', borderRadius: 12,
  padding: '14px 16px', minWidth: 320, maxWidth: 400,
  boxShadow: '0 4px 20px rgba(60,50,137,0.15)',
};

const INPUT_STYLE: React.CSSProperties = {
  width: '100%', border: '1px solid #AFA9EC',
  borderRadius: 6, padding: '6px 10px',
  fontSize: 12, fontFamily: 'monospace',
  marginBottom: 8, boxSizing: 'border-box',
  outline: 'none',
};

const RESOLVE_BTN: React.CSSProperties = {
  background: '#EEEDFE', color: '#3C3489',
  border: '1px solid #AFA9EC', borderRadius: 6,
  padding: '5px 12px', fontSize: 12,
  cursor: 'pointer', marginRight: 6,
};

const CLEAR_USER_BTN: React.CSSProperties = {
  background: '#FAC775', color: '#412402',
  border: 'none', borderRadius: 6,
  padding: '5px 12px', fontSize: 12, cursor: 'pointer',
};

const CONFIRM_OVERLAY: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const CONFIRM_BOX: React.CSSProperties = {
  background: '#fff', borderRadius: 12, padding: '20px 24px',
  minWidth: 320, boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
};

// CSS selectors for detecting card-level components in the page
const CARD_SELECTORS = [
  '[class*="card"]',
  '[class*="Card"]',
  '[class*="widget"]',
  '[class*="Widget"]',
  '[class*="stat"]',
  '[class*="Stat"]',
  '[class*="panel"]',
  '[class*="Panel"]',
  '[class*="dashboard"]',
  'article',
].join(', ');

interface CardPos { top: number; right: number }

function CacheComponentOverlay({ entries }: { entries: CacheKeyEntry[] }) {
  const [positions, setPositions] = useState<CardPos[]>([]);
  const updateRef = useRef<() => void>(() => undefined);

  const updatePositions = useCallback(() => {
    const allEls = Array.from(document.querySelectorAll<HTMLElement>(CARD_SELECTORS));
    const cards = allEls
      .filter(el => {
        const rect = el.getBoundingClientRect();
        if (rect.width < 160 || rect.height < 60) return false;
        if (rect.top < -300 || rect.bottom > window.innerHeight + 300) return false;
        // Only top-level cards — skip if any ancestor also matches
        const parent = el.parentElement;
        return parent ? !parent.closest(CARD_SELECTORS) : true;
      })
      .slice(0, entries.length)
      .map(el => {
        const rect = el.getBoundingClientRect();
        return {
          top: rect.top + 6,
          right: window.innerWidth - rect.right + 6,
        };
      });
    setPositions(cards);
  }, [entries.length]);

  updateRef.current = updatePositions;

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const debounced = () => {
      clearTimeout(timer);
      timer = setTimeout(() => updateRef.current(), 120);
    };
    // Initial run after paint settles
    requestAnimationFrame(() => requestAnimationFrame(() => updateRef.current()));
    window.addEventListener('scroll', debounced, { passive: true });
    window.addEventListener('resize', debounced);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('scroll', debounced);
      window.removeEventListener('resize', debounced);
    };
  }, []); // mount once; updateRef always points to latest updatePositions

  // Re-run when entries change (new keys → may need more/fewer badges)
  useEffect(() => {
    requestAnimationFrame(() => requestAnimationFrame(() => updateRef.current()));
  }, [entries.length]);

  if (!entries.length || !positions.length) return null;

  return (
    <>
      {positions.map((pos, i) => {
        const entry = entries[i];
        if (!entry) return null;
        const k = entry.key;
        const shortKey = k.length > 32 ? k.slice(0, 15) + '…' + k.slice(-13) : k;
        return (
          <div
            key={i}
            title={k}
            style={{
              position: 'fixed',
              top: pos.top,
              right: pos.right,
              zIndex: 9998,
              background: 'rgba(238,237,254,0.94)',
              border: '1.5px dashed #7F77DD',
              borderRadius: 5,
              padding: '2px 8px 2px 5px',
              fontSize: 10,
              fontFamily: 'monospace',
              color: '#3C3489',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
              boxShadow: '0 1px 4px rgba(60,50,137,0.12)',
              lineHeight: '18px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#7F77DD', flexShrink: 0 }} />
            {shortKey}
          </div>
        );
      })}
    </>
  );
}

export default function CacheDevPanel() {
  const {
    entries, resolvedUser, resolving, deleting,
    deleteKeys, deleteByUser, clearPage,
    resolveUser, clearResolvedUser,
  } = useCacheDevTools();

  const [expanded, setExpanded] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [urlInput, setUrlInput] = useState('');

  if (entries.length === 0 && !expanded) {
    return (
      <div style={FAB_STYLE}>
        <button style={{ ...FAB_BTN, opacity: 0.6, fontSize: 11 }} onClick={() => setExpanded(true)}>
          🔍 cache overlay active (0 keys)
        </button>
      </div>
    );
  }

  return (
    <>
      <CacheComponentOverlay entries={entries} />

      {confirmClear && (
        <div style={CONFIRM_OVERLAY}>
          <div style={CONFIRM_BOX}>
            <p style={{ marginBottom: 12, fontWeight: 500 }}>
              Xóa {entries.length} cache keys của page này?
            </p>
            <div style={{ maxHeight: 160, overflowY: 'auto', marginBottom: 14 }}>
              {entries.map(e => (
                <div key={e.key} style={{ fontSize: 11, fontFamily: 'monospace', marginBottom: 3, color: '#3C3489' }}>
                  • {e.key}
                </div>
              ))}
            </div>
            <button
              style={{ ...FAB_BTN, marginRight: 8 }}
              disabled={deleting}
              onClick={async () => { await clearPage(); setConfirmClear(false); }}
            >
              {deleting ? 'Đang xóa...' : 'Xác nhận xóa tất cả'}
            </button>
            <button style={RESOLVE_BTN} onClick={() => setConfirmClear(false)}>Hủy</button>
          </div>
        </div>
      )}

      <div style={FAB_STYLE}>
        {expanded && (
          <div style={PANEL_STYLE}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#3C3489' }}>
                Cache Dev Tools
              </span>
              <button style={{ ...X_BTN, color: '#888' }} onClick={() => setExpanded(false)}>✕</button>
            </div>

            <div style={{ marginBottom: 10, maxHeight: 200, overflowY: 'auto' }}>
              {entries.map(e => (
                <div key={e.key} style={BADGE_STYLE}>
                  <span style={DOT_STYLE} />
                  {e.key}
                  <button
                    style={X_BTN}
                    disabled={deleting}
                    onClick={() => deleteKeys([e.key])}
                    title="Xóa cache key này"
                  >✕</button>
                </div>
              ))}
            </div>

            <hr style={{ borderColor: '#EEE', margin: '10px 0' }} />

            <div style={{ fontSize: 11, color: '#555', marginBottom: 6, fontWeight: 500 }}>
              Xóa cache cho user khác
            </div>
            <input
              style={INPUT_STYLE}
              placeholder="Paste URL từ user gặp lỗi..."
              value={urlInput}
              onChange={e => setUrlInput(e.target.value)}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <button
                style={RESOLVE_BTN}
                disabled={resolving || !urlInput}
                onClick={() => resolveUser(urlInput)}
              >
                {resolving ? 'Đang tìm...' : 'Resolve user'}
              </button>
              {resolvedUser && (
                <>
                  <span style={{ fontSize: 11, color: '#3C3489', background: '#EEEDFE', borderRadius: 4, padding: '3px 8px' }}>
                    {resolvedUser.display_name} (#{resolvedUser.user_id})
                  </span>
                  <button
                    style={CLEAR_USER_BTN}
                    disabled={deleting}
                    onClick={async () => {
                      await deleteByUser(entries.map(e => e.pattern), resolvedUser.user_id);
                      clearResolvedUser();
                    }}
                  >
                    {deleting ? 'Đang xóa...' : 'Clear cho user này'}
                  </button>
                  <button style={{ ...X_BTN, color: '#aaa' }} onClick={clearResolvedUser}>✕</button>
                </>
              )}
            </div>
          </div>
        )}

        <button style={FAB_BTN} onClick={() => { setExpanded(v => !v); }}>
          {expanded ? '✕' : '🗑'} Cache keys
          <span style={COUNT_BADGE}>{entries.length} keys</span>
        </button>
        {entries.length > 0 && (
          <button
            style={{ ...FAB_BTN, background: '#A32D2D', fontSize: 11, padding: '6px 14px' }}
            onClick={() => setConfirmClear(true)}
          >
            Xóa tất cả ngay
          </button>
        )}
      </div>
    </>
  );
}
